/**
 * PPM Settings Tab Component
 * Handles PPM/PPK calculation settings and score mappings
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { RotateCcw, AlertTriangle, Info } from 'lucide-react';
import { SupplierPerformanceSettings } from '../../../lib/api';
import { PerformanceFormData } from './types';
import { useQualityMeasureLabel, useQualityMeasureLongLabel } from '../../../lib/qualityLabels';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';

interface PPMSettingsTabProps {
  settings: SupplierPerformanceSettings | null;
  formData: PerformanceFormData;
  onInputChange: (field: string, value: string | number | boolean) => void;
  onResetPPMMappings: () => void;
}

export function PPMSettingsTab({
  settings,
  formData,
  onInputChange,
  onResetPPMMappings,
}: PPMSettingsTabProps) {
  const measure = useQualityMeasureLabel();
  const measureLong = useQualityMeasureLongLabel();
  // Prefer the raw boolean from MenuSettingsContext over comparing against
  // the 'PPK' string — keeps the toggle semantics single-source-of-truth.
  const { qualityMeasurePpk } = useMenuSettings();
  const divisorLabel = qualityMeasurePpk ? '1,000' : '1,000,000';
  const unitNoun = qualityMeasurePpk ? 'thousand' : 'million';
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            <CardTitle>{measure} ({measureLong}) Settings</CardTitle>
          </div>
          <CardDescription>
            Configure how {measure} quality metrics are calculated and displayed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Thresholds */}
          <div className="space-y-4">
            <h3 className="font-medium">{measure} Color Thresholds</h3>
            <p className="text-sm text-muted-foreground">
              Set the {measure} value thresholds for color coding on dashboards. Lower {measure} is better.
            </p>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="ppm_excellent_threshold" className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  Excellent (Green)
                </Label>
                <div className="flex items-center gap-2">
                  <span>&lt;</span>
                  <Input
                    id="ppm_excellent_threshold"
                    type="number"
                    min="0"
                    value={formData.ppm_excellent_threshold}
                    onChange={(e) => onInputChange('ppm_excellent_threshold', parseInt(e.target.value) || 0)}
                  />
                  <span>{measure}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ppm_good_threshold" className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                  Good (Yellow)
                </Label>
                <div className="flex items-center gap-2">
                  <span>&lt;</span>
                  <Input
                    id="ppm_good_threshold"
                    type="number"
                    min="0"
                    value={formData.ppm_good_threshold}
                    onChange={(e) => onInputChange('ppm_good_threshold', parseInt(e.target.value) || 0)}
                  />
                  <span>{measure}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ppm_poor_threshold" className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500"></span>
                  Poor (Red)
                </Label>
                <div className="flex items-center gap-2">
                  <span>&gt;</span>
                  <Input
                    id="ppm_poor_threshold"
                    type="number"
                    min="0"
                    value={formData.ppm_poor_threshold}
                    onChange={(e) => onInputChange('ppm_poor_threshold', parseInt(e.target.value) || 0)}
                  />
                  <span>{measure}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Formula Info */}
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              <span className="font-medium">{measure} Calculation Formula</span>
            </div>
            <code className="block text-sm p-2 bg-background rounded border">
              {measure} = (Reject Quantity / Total Received Quantity) x {divisorLabel}
            </code>
            <p className="text-sm text-muted-foreground">
              {measure} measures the defect rate per {unitNoun} parts. Lower values indicate better quality performance.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Score Mappings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{measure} Score Mappings</CardTitle>
              <CardDescription>
                Map {measure} values to score points for rating calculations.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={onResetPPMMappings}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {settings?.ppm_score_mappings && settings.ppm_score_mappings.length > 0 ? (
            <div className="space-y-2">
              <div className="grid grid-cols-4 gap-4 text-sm font-medium text-muted-foreground pb-2 border-b">
                <div>Min {measure}</div>
                <div>Max {measure}</div>
                <div>Score Points</div>
                <div></div>
              </div>
              {settings.ppm_score_mappings.map((mapping) => (
                <div key={mapping.id} className="grid grid-cols-4 gap-4 items-center py-2">
                  <div>{mapping.min_ppm.toLocaleString()}</div>
                  <div>{mapping.max_ppm.toLocaleString()}</div>
                  <div className="font-medium">{mapping.mapped_score} pts</div>
                  <div>
                    <Badge variant="outline">
                      {mapping.max_ppm <= 500 ? 'Excellent' :
                       mapping.max_ppm <= 2000 ? 'Good' :
                       mapping.max_ppm <= 5000 ? 'Fair' : 'Poor'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">
              No {measure} score mappings configured. Click "Reset to Defaults" to create default mappings.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
